<template>
  <transition
    name="slide-x-reverse-transition"
    :css="!sidebarMode && !closingSidebar"
  >
    <div
      v-if="value"
      ref="container"
      class="dockable-panel"
      :class="[containerClasses, panelClass]"
      :style="containerStyle"
    >
      <div class="dockable-panel__header" @click="handleHeaderClick">
        <slot name="title" />

        <v-spacer />
        <slot name="header-actions" />

        <template v-if="isSidebar">
          <v-btn
            v-if="expandable"
            icon
            small
            class="mr-1"
            :title="expanded ? 'Collapse' : 'Expand'"
            @click.stop="$emit('update:expanded', !expanded)"
          >
            <v-icon color="light" small>
              {{
                expanded
                  ? 'mdi-arrow-collapse-horizontal'
                  : 'mdi-arrow-expand-horizontal'
              }}
            </v-icon>
          </v-btn>

          <v-btn
            icon
            small
            class="mr-1"
            title="Switch to floating"
            @click.stop="undock"
          >
            <v-icon color="light" small>mdi-window-restore</v-icon>
          </v-btn>

          <v-btn icon class="mr-1" @click.stop="close">
            <v-icon color="light">close</v-icon>
          </v-btn>
        </template>

        <template v-else>
          <v-btn
            v-if="!isMobile && !isCollapsed"
            icon
            small
            class="mr-1"
            :title="isLarge ? 'Minimize' : 'Maximize'"
            @click.stop="toggleSize"
          >
            <v-icon color="light" small>
              {{ isLarge ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}
            </v-icon>
          </v-btn>

          <v-btn
            v-if="!isMobile"
            icon
            small
            class="mr-1"
            :title="isCollapsed ? 'Expand' : 'Collapse'"
            @click.stop="toggleCollapse"
          >
            <v-icon color="light" small>
              {{ isCollapsed ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>

          <v-btn
            v-if="!isMobile"
            icon
            small
            class="mr-1"
            :disabled="!sidebarAllowed"
            title="Dock to sidebar"
            @click.stop="switchToSidebar"
          >
            <v-icon color="light" small>mdi-dock-right</v-icon>
          </v-btn>

          <v-btn icon class="mr-1" @click.stop="close">
            <v-icon color="light">close</v-icon>
          </v-btn>
        </template>
      </div>

      <v-expand-transition>
        <div v-show="!isCollapsed" class="dockable-panel__content">
          <slot />
        </div>
      </v-expand-transition>

      <slot v-if="isCollapsed" name="collapsed" />
    </div>
  </transition>
</template>

<script>
import EventBus from '@/factories/eventBus';
import { markPanelOpen } from './openPanels';

const DISPLAY_MODE = {
  LARGE: 'large',
  SMALL: 'small',
  COLLAPSED: 'collapsed',
};

const LAYOUT = {
  MARGIN: 20,
  HEADER_HEIGHT: 52,
  LARGE_TOP: 65,
  LARGE_WIDTH: 520,
  SMALL_WIDTH: 440,
  SMALL_HEIGHT: 580,
  COLLAPSED_WIDTH: 320,
  SIDEBAR_WIDTH: 400,
};

const ANIMATION_DURATION = 350;
const ANIMATION_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

export default {
  name: 'DockablePanel',
  props: {
    value: Boolean,
    allowSidebar: {
      type: Boolean,
      default: true,
    },
    panelClass: {
      type: [String, Array, Object],
      default: '',
    },
    sidebarWidth: {
      type: Number,
      default: LAYOUT.SIDEBAR_WIDTH,
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      displayMode: DISPLAY_MODE.SMALL,
      preferredMode: DISPLAY_MODE.SMALL,
      previousMode: DISPLAY_MODE.SMALL,
      sidebarMode: false,
      sidebarAllowed: this.allowSidebar,
      closingSidebar: false,
      isAnimating: false,
      currentDimensions: null,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
      resizeRafId: null,
      lastSidebarState: false,
    };
  },
  computed: {
    isLarge() {
      return this.displayMode === DISPLAY_MODE.LARGE;
    },
    isSmall() {
      return this.displayMode === DISPLAY_MODE.SMALL;
    },
    isCollapsed() {
      return this.displayMode === DISPLAY_MODE.COLLAPSED;
    },
    isSidebar() {
      return (this.sidebarMode || this.closingSidebar) && !this.isMobile;
    },
    containerClasses() {
      return {
        'dockable-panel--mobile': this.isMobile,
        'dockable-panel--large': this.isLarge && !this.isMobile,
        'dockable-panel--small': this.isSmall && !this.isMobile,
        'dockable-panel--collapsed': this.isCollapsed && !this.isMobile,
        'dockable-panel--animating': this.isAnimating,
        'dockable-panel--sidebar': this.isSidebar,
      };
    },
    targetDimensions() {
      const vh = this.viewportHeight;
      const vw = this.viewportWidth;

      if (this.isMobile) {
        return { top: 0, right: 0, width: vw, height: vh };
      }
      if (this.isCollapsed) {
        return {
          top: vh - LAYOUT.HEADER_HEIGHT - LAYOUT.MARGIN,
          right: LAYOUT.MARGIN,
          width: LAYOUT.COLLAPSED_WIDTH,
          height: LAYOUT.HEADER_HEIGHT,
        };
      }
      if (this.isLarge) {
        return {
          top: LAYOUT.LARGE_TOP,
          right: LAYOUT.MARGIN,
          width: LAYOUT.LARGE_WIDTH,
          height: vh - LAYOUT.LARGE_TOP - LAYOUT.MARGIN,
        };
      }
      return {
        top: vh - LAYOUT.SMALL_HEIGHT - LAYOUT.MARGIN,
        right: LAYOUT.MARGIN,
        width: LAYOUT.SMALL_WIDTH,
        height: LAYOUT.SMALL_HEIGHT,
      };
    },
    containerStyle() {
      if (this.isMobile) return {};

      const dims = this.currentDimensions || this.targetDimensions;
      if (this.isSidebar) {
        if (this.expanded) {
          return { flex: '1 1 auto', width: 'auto', minWidth: 0 };
        }
        return { width: `${this.sidebarWidth}px` };
      }
      if (this.currentDimensions) {
        return {
          position: 'fixed',
          top: `${dims.top}px`,
          right: `${dims.right}px`,
          width: `${dims.width}px`,
          height: `${dims.height}px`,
          bottom: 'auto',
          margin: 0,
        };
      }
      if (this.isSmall || this.isCollapsed) {
        return {
          position: 'fixed',
          bottom: `${LAYOUT.MARGIN}px`,
          right: `${LAYOUT.MARGIN}px`,
          width: `${dims.width}px`,
          height: `${dims.height}px`,
          top: 'auto',
          margin: 0,
        };
      }
      return {
        position: 'fixed',
        top: `${LAYOUT.LARGE_TOP}px`,
        bottom: `${LAYOUT.MARGIN}px`,
        right: `${LAYOUT.MARGIN}px`,
        width: `${LAYOUT.LARGE_WIDTH}px`,
        height: 'auto',
        margin: 0,
      };
    },
  },
  watch: {
    isSidebar(active) {
      this.emitSidebarState(active);
    },
    value(visible) {
      markPanelOpen(this._uid, visible);
      if (visible) {
        EventBus.$emit('DockablePanel.opened', this._uid);
      } else {
        this.beginClose();
      }
    },
    allowSidebar(allowed) {
      if (!allowed) {
        this.sidebarAllowed = false;
        this.sidebarMode = false;
      } else {
        this.sidebarAllowed = true;
      }
    },
    displayMode(mode) {
      this.$emit('mode-change', mode);
    },
  },
  created() {
    this.activeAnimation = null;
    this.syncViewportSize();
    this.handleViewportResizeBound = this.handleViewportResize.bind(this);
    window.addEventListener('resize', this.handleViewportResizeBound);
    EventBus.$on('DockablePanel.opened', this.handleOtherPanelOpened);
    markPanelOpen(this._uid, this.value);
  },
  beforeDestroy() {
    markPanelOpen(this._uid, false);
    this.cleanupAnimation();
    this.emitSidebarState(false);
    if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);
    window.removeEventListener('resize', this.handleViewportResizeBound);
    EventBus.$off('DockablePanel.opened', this.handleOtherPanelOpened);
  },
  methods: {
    // Opening a panel closes any other visible panel.
    handleOtherPanelOpened(uid) {
      if (uid !== this._uid && this.value) {
        this.$emit('input', false);
      }
    },

    prepareOpen(options = {}) {
      this.cleanupAnimation();
      const wasCollapsed = this.value && this.isCollapsed;
      this.sidebarAllowed =
        options.allowSidebar === undefined
          ? this.allowSidebar
          : options.allowSidebar;
      this.sidebarMode = this.sidebarAllowed && !!options.sidebar;
      this.closingSidebar = false;

      if (wasCollapsed) {
        this.animateToMode(this.preferredMode);
      } else if (this.displayMode !== this.preferredMode) {
        this.displayMode = this.preferredMode;
      }
    },
    // Only the close buttons call this.
    close() {
      this.$emit('user-close');
      this.$emit('input', false);
    },
    beginClose() {
      this.cleanupAnimation();
      if (!this.sidebarMode) return;

      this.closingSidebar = true;
      this.emitSidebarState(false);
      this.sidebarMode = false;
      this.$nextTick(() => {
        this.closingSidebar = false;
      });
    },
    emitSidebarState(active) {
      if (this.lastSidebarState === active) return;
      this.lastSidebarState = active;
      this.$emit('sidebar-change', active);
    },
    // Only the "Switch to floating" button calls this.
    undock() {
      this.$emit('user-undock');
      this.switchToFloating();
    },
    switchToFloating() {
      this.sidebarMode = false;
    },
    switchToSidebar() {
      if (this.sidebarAllowed) this.sidebarMode = true;
    },
    toggleSize() {
      const mode = this.isLarge ? DISPLAY_MODE.SMALL : DISPLAY_MODE.LARGE;
      this.preferredMode = mode;
      this.animateToMode(mode);
    },
    toggleCollapse() {
      if (!this.isCollapsed) this.previousMode = this.displayMode;
      this.animateToMode(
        this.isCollapsed ? this.preferredMode : DISPLAY_MODE.COLLAPSED
      );
    },
    handleHeaderClick() {
      if (this.isCollapsed) this.toggleCollapse();
    },
    async animateToMode(mode) {
      if (this.isAnimating) return;
      if (this.isMobile || !this.$refs.container) {
        this.displayMode = mode;
        return;
      }

      const first = this.targetDimensions;
      this.currentDimensions = { ...first };
      this.displayMode = mode;
      await this.$nextTick();
      const last = this.targetDimensions;

      if (
        first.top === last.top &&
        first.width === last.width &&
        first.height === last.height
      ) {
        this.currentDimensions = null;
        this.$emit('transition-end', mode);
        return;
      }

      this.isAnimating = true;
      const animation = this.$refs.container.animate(
        [
          {
            top: `${first.top}px`,
            width: `${first.width}px`,
            height: `${first.height}px`,
          },
          {
            top: `${last.top}px`,
            width: `${last.width}px`,
            height: `${last.height}px`,
          },
        ],
        {
          duration: ANIMATION_DURATION,
          easing: ANIMATION_EASING,
          fill: 'forwards',
        }
      );
      this.activeAnimation = animation;

      animation.onfinish = () => {
        if (this.activeAnimation !== animation) return;
        this.activeAnimation = null;
        this.currentDimensions = null;
        this.isAnimating = false;
        this.$nextTick(() => {
          animation.cancel();
          if (this.value && this.displayMode === mode) {
            this.$emit('transition-end', mode);
          }
        });
      };
      animation.oncancel = () => {
        if (this.activeAnimation !== animation) return;
        this.activeAnimation = null;
        this.currentDimensions = null;
        this.isAnimating = false;
      };
    },
    cleanupAnimation() {
      const animation = this.activeAnimation;
      this.activeAnimation = null;
      this.currentDimensions = null;
      this.isAnimating = false;
      if (!animation) return;

      animation.onfinish = null;
      animation.oncancel = null;
      animation.cancel();
    },
    syncViewportSize() {
      if (typeof window === 'undefined') return;
      this.viewportHeight = window.innerHeight;
      this.viewportWidth = window.innerWidth;
    },
    handleViewportResize() {
      if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = requestAnimationFrame(() => {
        this.resizeRafId = null;
        this.syncViewportSize();
        if (!this.isMobile && !this.isAnimating) {
          this.currentDimensions = null;
        }
      });
    },
  },
};
</script>

<style lang="less">
.dockable-panel {
  position: fixed;
  max-height: calc(100vh - 40px) !important;
  background: var(--v-lightGray-base);
  border-radius: @base-radius;
  box-shadow: @base-shadow4;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 2147483600;

  &--animating {
    overflow: hidden;
  }

  &--collapsed &__header {
    cursor: pointer;

    &:hover {
      filter: brightness(1.1);
    }
  }

  &--sidebar {
    position: static;
    flex-shrink: 0;
    height: 100%;
    max-height: 100% !important;
    border-radius: 0;
    border-left: 1px solid var(--v-darkGray-base);
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.08);
    z-index: 5;
  }

  &--mobile {
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    max-height: 100% !important;
    border-radius: 0;
    box-shadow: none;
  }

  &__header {
    height: 58px;
    padding: 11px 8px 11px 16px;
    background: var(--v-appMain-lighten1);
    font-family: var(--v-headers-font-base);
    font-weight: 600;
    display: flex;
    align-items: center;
    color: #fff;
    font-size: 20px;
    box-shadow: @base-shadow2;
    z-index: 1;
    flex-shrink: 0;
    transition: filter 0.15s ease;

    svg {
      fill: #fff;
    }

    .v-btn {
      transition: transform 0.15s ease, opacity 0.15s ease;

      &:hover {
        transform: scale(1.1);
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
}
</style>
